/**
 * Comprehensive Color Utilities for Visualizer Component
 * Combines all color manipulation, conversion, and accessibility functions
 */

// ============= BASIC COLOR CONVERSIONS =============

// Convert hex to RGB object
export const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
};

// Convert hex to RGBA string
export const hexToRgba = (hex, alpha = 1) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Get overlay style for images
export const getOverlayStyle = (color, opacity = 0.6) => {
    const rgb = hexToRgb(color);
    return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})` : `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
};

// Convert RGB to Hex
export const rgbToHex = (r, g, b) => {
    const toHex = (c) => {
        const hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };
    return "#" + toHex(r) + toHex(g) + toHex(b);
};

// ============= ADVANCED COLOR BLINDNESS SIMULATION =============

/**
 * Simulate color blindness using scientifically accurate transformation matrices
 * Updated with more accurate matrices based on Machado, Oliveira and Fernandes (2009)
 * and Brettel, Vienot and Mollon (1997) research
 */
export const simulateColorBlindness = (hex, type) => {
    if (type === 'normal' || !type) return hex;

    const rgb = hexToRgb(hex);
    if (!rgb) return hex;

    // Normalize RGB values to 0-1 range
    let [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255];

    // More accurate transformation matrices for different types of color blindness
    const matrices = {
        // Protanopia (Red-blind) - Complete absence of L-cones
        protanopia: [
            [0.152286, 1.052583, -0.204868],
            [0.114503, 0.786281, 0.099216],
            [-0.003882, -0.048116, 1.051998]
        ],
        // Deuteranopia (Green-blind) - Complete absence of M-cones
        deuteranopia: [
            [0.367322, 0.860646, -0.227968],
            [0.280085, 0.672501, 0.047413],
            [-0.011820, 0.042940, 0.968881]
        ],
        // Tritanopia (Blue-blind) - Complete absence of S-cones  
        tritanopia: [
            [1.255528, -0.076749, -0.178779],
            [-0.078411, 0.930809, 0.147602],
            [0.004733, 0.691367, 0.303900]
        ],
        // Protanomaly (Red-deficient) - Shifted L-cone sensitivity
        protanomaly: [
            [0.458064, 0.679578, -0.137642],
            [0.092785, 0.846313, 0.060902],
            [-0.007494, -0.016807, 1.024301]
        ],
        // Deuteranomaly (Green-deficient) - Shifted M-cone sensitivity
        deuteranomaly: [
            [0.547494, 0.607765, -0.155259],
            [0.181692, 0.781742, 0.036566],
            [-0.010410, 0.027275, 0.983136]
        ],
        // Tritanomaly (Blue-deficient) - Shifted S-cone sensitivity
        tritanomaly: [
            [1.017277, 0.027029, -0.044306],
            [-0.006113, 0.958479, 0.047634],
            [0.006379, 0.248708, 0.744913]
        ],
        // Achromatopsia (Complete color blindness) - Only rod vision
        achromatopsia: [
            [0.212656, 0.715158, 0.072186],
            [0.212656, 0.715158, 0.072186],
            [0.212656, 0.715158, 0.072186]
        ],
        // Achromatomaly (Blue cone monochromacy) - Partial color vision
        achromatomaly: [
            [0.618020, 0.320560, 0.061420],
            [0.163312, 0.775791, 0.060897],
            [0.047684, 0.108946, 0.843370]
        ]
    };

    const matrix = matrices[type];
    if (!matrix) return hex;

    // Apply transformation matrix
    const newR = matrix[0][0] * r + matrix[0][1] * g + matrix[0][2] * b;
    const newG = matrix[1][0] * r + matrix[1][1] * g + matrix[1][2] * b;
    const newB = matrix[2][0] * r + matrix[2][1] * g + matrix[2][2] * b;

    // Convert back to 0-255 range and clamp values
    const clampedR = Math.max(0, Math.min(255, Math.round(newR * 255)));
    const clampedG = Math.max(0, Math.min(255, Math.round(newG * 255)));
    const clampedB = Math.max(0, Math.min(255, Math.round(newB * 255)));

    // Convert back to hex
    return rgbToHex(clampedR, clampedG, clampedB);
};

// Apply color blindness filter to entire color array
export const applyColorBlindnessFilter = (colors, colorBlindnessType) => {
    if (colorBlindnessType === 'normal' || !colorBlindnessType) return colors;

    return colors.map(color => ({
        ...color,
        value: simulateColorBlindness(color.value, colorBlindnessType)
    }));
};

// ============= UTILITY FUNCTIONS =============

// Get color by index with fallback
export const getColor = (colors, index, fallback = '#3b82f6') => {
    return colors[index]?.value || colors[0]?.value || fallback;
};

// Check if color is light or dark (for contrast)
export const isLightColor = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return false;

    // Calculate relative luminance
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance > 0.5;
};

// Get contrasting text color (black or white)
export const getContrastingTextColor = (hex) => {
    return isLightColor(hex) ? '#000000' : '#ffffff';
};

// ============= HSL CONVERSIONS =============

// Convert hex to HSL
export const hexToHsl = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return [0, 0, 0];

    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

// Convert HSL to hex
export const hslToHex = (h, s, l) => {
    h = h % 360;
    s = Math.max(0, Math.min(100, s)) / 100;
    l = Math.max(0, Math.min(100, l)) / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r, g, b;

    if (0 <= h && h < 60) {
        r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
        r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
        r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
        r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
        r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
        r = c; g = 0; b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return rgbToHex(r, g, b);
};